import React from "react";
import { Field, FieldArray, useFormikContext } from "formik";
import { Button, FormGroup, Label } from "reactstrap";

export const emptyTimePrice = () => ({
  pricingType: "OFF_PEAK",
  startTime: "",
  endTime: "",
  price: "",
});

const fieldError = (errors, touched, index, field) =>
  touched?.pricingIntervals?.[index]?.[field] &&
  errors?.pricingIntervals?.[index]?.[field];

export const PeakOffPeakPricing = () => {
  const { values, errors, touched } = useFormikContext();
  const intervals = values.pricingIntervals || [];

  return (
    <FieldArray name="pricingIntervals">
      {({ push, remove }) => (
        <div className="peak-off-peak-pricing border rounded p-3 mt-3 mb-3">
          {intervals.map((interval, index) => (
            <div className="peak-off-peak-pricing__interval" key={index}>
              <div className="peak-off-peak-pricing__interval-header">
                <strong>Pricing interval {index + 1}</strong>
                {intervals.length > 1 && (
                  <Button
                    type="button"
                    className="peak-off-peak-pricing__remove"
                    onClick={() => remove(index)}
                  >
                    Remove
                  </Button>
                )}
              </div>
              <div className="peak-off-peak-pricing__row">
                <FormGroup className="mb-0">
                  <Label>Type</Label>
                  <Field
                    component="select"
                    name={`pricingIntervals.${index}.pricingType`}
                    className="form-control"
                  >
                    <option value="OFF_PEAK">Off-peak</option>
                    <option value="PEAK">Peak</option>
                  </Field>
                </FormGroup>
              {["startTime", "endTime"].map((field) => (
                <FormGroup className="mb-0" key={field}>
                    <Label>{field === "startTime" ? "From" : "To"}</Label>
                    <Field
                      type="time"
                      name={`pricingIntervals.${index}.${field}`}
                      className={`form-control ${fieldError(
                        errors,
                        touched,
                        index,
                        field
                      )
                        ? "is-invalid"
                        : ""}`}
                    />
                    {fieldError(errors, touched, index, field) && (
                      <div className="invalid-feedback">
                        {errors.pricingIntervals[index][field]}
                      </div>
                    )}
                </FormGroup>
              ))}
                <FormGroup className="mb-0">
                  <Label>Price</Label>
                  <Field
                    type="number"
                    min="0.01"
                    step="0.01"
                    name={`pricingIntervals.${index}.price`}
                    className={`form-control ${fieldError(
                      errors,
                      touched,
                      index,
                      "price"
                    )
                      ? "is-invalid"
                      : ""}`}
                  />
                  {fieldError(errors, touched, index, "price") && (
                    <div className="invalid-feedback">
                      {errors.pricingIntervals[index].price}
                    </div>
                  )}
                </FormGroup>
              </div>
            </div>
          ))}
          <Button
            type="button"
            color="primary"
            outline
            onClick={() => push(emptyTimePrice())}
          >
            Add pricing interval
          </Button>
          {typeof errors.pricingIntervals === "string" && (
            <div className="text-danger mt-2">{errors.pricingIntervals}</div>
          )}
          <small className="text-muted d-block mt-2">
            Add only the hours this package may be used. Intervals may cross
            midnight but cannot overlap.
          </small>
        </div>
      )}
    </FieldArray>
  );
};

export const buildPeakOffPeakPrices = (values) =>
  values.hasPeakOffPeakPricing
    ? (values.pricingIntervals || []).map((interval) => ({
        pricingType: interval.pricingType,
        startTime: interval.startTime,
        endTime: interval.endTime,
        price: Number(interval.price),
      }))
    : [];

const expandRange = (startTime, endTime) => {
  const toMinutes = (time) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };
  const start = toMinutes(startTime);
  const end = toMinutes(endTime);
  return start < end ? [[start, end]] : [[start, 1440], [0, end]];
};

export const timePricingSchema = (Yup) =>
  Yup.array()
    .when("hasPeakOffPeakPricing", {
      is: true,
      then: Yup.array()
        .of(
          Yup.object().shape({
            pricingType: Yup.string()
              .oneOf(["PEAK", "OFF_PEAK"])
              .required("Type is required"),
            startTime: Yup.string().required("Start time is required"),
            endTime: Yup.string()
              .required("End time is required")
              .test("different-time", "Times must be different", function(value) {
                return !value || !this.parent.startTime || value !== this.parent.startTime;
              }),
            price: Yup.number()
              .typeError("Price is required")
              .positive("Price must be greater than zero")
              .required("Price is required"),
          })
        )
        .min(1, "Add at least one pricing interval")
        .test("no-overlap", "Pricing intervals cannot overlap", (rules = []) => {
          if (rules.some((rule) => !rule.startTime || !rule.endTime)) return true;
          const ranges = rules.map((rule) =>
            expandRange(rule.startTime, rule.endTime)
          );
          return !ranges.some((first, index) =>
            ranges.slice(index + 1).some((second) =>
              first.some((a) =>
                second.some((b) => a[0] < b[1] && a[1] > b[0])
              )
            )
          );
        }),
    })
    .default([]);
